import type {
  IProductionPlanSupplierMutationResponse,
  IProductionPlanSupplierResponse,
  ProductionPlanSupplierFilters,
} from "features/production-plan-supplier/types";
import {
  buildProductionPlanSupplierExportQuery,
  buildProductionPlanSupplierGridQuery,
} from "features/production-plan-supplier/utils";
import Fetch from "utils/fetch";

interface ProductionPlanSupplierHeaderPayload {
  currentStock: number;
  forecast: number | null;
  useFilterDefault: boolean;
}

interface ProductionPlanSupplierDayPayload {
  productionDate: string;
  productionQuantity: number | null;
  useFilterDefault: boolean;
}

export const getProductionPlanSuppliers = async (
  filters: ProductionPlanSupplierFilters
): Promise<IProductionPlanSupplierResponse> => {
  const query = buildProductionPlanSupplierGridQuery(filters);
  return Fetch.get(`supplier-production-plans:by-filters?${query}`).then(({ data }) =>
    normalizeProductionPlanSupplierResponse(data)
  );
};

export const getProductionPlanSupplierExportModel = () =>
  Fetch.get("supplier-production-plans/export-model").then(({ data }) => data);

export const getProductionPlanSupplierExport = (filters: ProductionPlanSupplierFilters) => {
  const query = buildProductionPlanSupplierExportQuery(filters);
  return Fetch.get(`supplier-production-plans/export-data?${query}`).then(({ data }) => data);
};

export const updateProductionPlanSupplierHeader = async (
  headerId: number,
  payload: ProductionPlanSupplierHeaderPayload
): Promise<IProductionPlanSupplierMutationResponse> =>
  Fetch.put(`supplier-production-plans/headers/${headerId}`, payload).then(({ data }) =>
    normalizeProductionPlanSupplierMutationResponse(data)
  );

export const updateProductionPlanSupplierDay = async (
  headerId: number,
  payload: ProductionPlanSupplierDayPayload
): Promise<IProductionPlanSupplierMutationResponse> =>
  Fetch.put(`supplier-production-plans/headers/${headerId}/days`, payload).then(({ data }) =>
    normalizeProductionPlanSupplierMutationResponse(data)
  );

export const deleteProductionPlanSupplierHeader = (headerId: number, useFilterDefault: boolean) =>
  Fetch.delete(`supplier-production-plans/headers/${headerId}`, { useFilterDefault }).then(
    ({ data }) => data
  );

type RawRecord = Record<string, unknown>;

const read = (record: RawRecord, camel: string, pascal: string) =>
  record[camel] ?? record[pascal];

const normalizeProductionPlanSupplierRow = (row: RawRecord) => ({
  Id: read(row, "id", "Id") as number,
  SupplierId: read(row, "supplierId", "SupplierId") as number,
  SupplierCode: String(read(row, "supplierCode", "SupplierCode") ?? ""),
  SupplierDescription: (read(row, "supplierDescription", "SupplierDescription") as string) ?? "",
  MaterialNumber:
    read(row, "materialNumber", "MaterialNumber") === null
      ? null
      : String(read(row, "materialNumber", "MaterialNumber") ?? ""),
  MaterialDescription: read(row, "materialDescription", "MaterialDescription") as string | null,
  Family: read(row, "family", "Family") as string | null,
  LabeledFamilyId: (read(row, "labeledFamilyId", "LabeledFamilyId") as number | null) ?? null,
  LabeledFamilyName: (read(row, "labeledFamilyName", "LabeledFamilyName") as string | null) ?? null,
  SupplierOriginId: (read(row, "supplierOriginId", "SupplierOriginId") as number | null) ?? null,
  SupplierOriginName: (read(row, "supplierOriginName", "SupplierOriginName") as string | null) ?? null,
  CurrentStock: read(row, "currentStock", "CurrentStock") as number,
  Forecast: (read(row, "forecast", "Forecast") as number | null) ?? null,
  LastUpdate: read(row, "lastUpdate", "LastUpdate") as string,
  ProductionDates: (() => {
    const productionDates = read(row, "productionDates", "ProductionDates");

    return Array.isArray(productionDates)
      ? productionDates.map((productionDate) => {
          const record = productionDate as RawRecord;

          return {
            ProductionDate: read(record, "productionDate", "ProductionDate") as string,
            ProductionQuantity:
              (read(record, "productionQuantity", "ProductionQuantity") as number | null) ?? null,
          };
        })
      : [];
  })(),
});

const normalizeProductionPlanSupplierResponse = (data: RawRecord): IProductionPlanSupplierResponse => ({
  CurrentPage: (read(data, "currentPage", "CurrentPage") as number | null) ?? 0,
  TotalItems: (read(data, "totalItems", "TotalItems") as number | null) ?? 0,
  TotalPages: (read(data, "totalPages", "TotalPages") as number | null) ?? 0,
  Data: Array.isArray(read(data, "data", "Data"))
    ? (read(data, "data", "Data") as RawRecord[]).map(normalizeProductionPlanSupplierRow)
    : [],
});

const normalizeProductionPlanSupplierMutationResponse = (
  data: RawRecord
): IProductionPlanSupplierMutationResponse => ({
  Id: read(data, "id", "Id") as number,
  CurrentStock: read(data, "currentStock", "CurrentStock") as number,
  Forecast: (read(data, "forecast", "Forecast") as number | null) ?? null,
  LastUpdate: read(data, "lastUpdate", "LastUpdate") as string,
  ProductionDate: (read(data, "productionDate", "ProductionDate") as string | null) ?? null,
  ProductionQuantity:
    (read(data, "productionQuantity", "ProductionQuantity") as number | null) ?? null,
});
